import { Box, Select, SelectProps, useMergeRefs, useStyleConfig } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { ChangeEvent, FocusEvent, forwardRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useFieldValidationStates } from '../hooks/useFieldValidationStates';
import { SimpleField, SimpleXProps } from './SimpleField';
import { useSimpleFormContext } from './SimpleForm';

export type ValidValue = string | number | readonly string[] | undefined;

export type Option = {
  label: string;
  value: string | number;
  disabled?: boolean;
};

export type OptionGroupOption = {
  groupLabel: string;
  options: Option[];
};

export type SimpleSelectProps = SimpleXProps &
  SelectProps & {
    colorScheme?: string;
    selectProps?: SelectProps;
    options?: Option[];
    optionGroupOptions?: OptionGroupOption[];
  };

export const SimpleSelect = forwardRef(
  (
    {
      name,
      label,
      helper,
      size = 'md',
      placeholder = '-',
      ignoreOnBlur,
      borderRadius = 'md',
      height,
      selectProps = {},
      options = [],
      optionGroupOptions = [],
      ...rest
    }: SimpleSelectProps,
    ref,
  ) => {
    const { simpleOnBlur } = useSimpleFormContext();
    const { control, register, setValue } = useFormContext();
    const value = useWatch({ name, control });
    const { isInvalid } = useFieldValidationStates(name as string);

    const formProps = register(name, {
      setValueAs: (value: ValidValue) => {
        if (value === '') return null;

        return value;
      },
    });
    const mergedRef = useMergeRefs(ref, formProps.ref);

    const onBlur = (event: FocusEvent<HTMLSelectElement>) => {
      if (ignoreOnBlur) return;

      formProps.onBlur(event);
      simpleOnBlur?.(event);
    };

    const onChange = (event: ChangeEvent<HTMLSelectElement>) => {
      // the placeholder's value is an empty string because select values can't be null
      // here we map the empty string to null
      if (event.target.value === '') {
        setValue(name, null);
        return;
      }
      formProps.onChange(event);
      rest?.onChange?.(event);
    };

    const defaultStyles = useStyleConfig('Select', { variant: rest?.variant });

    return (
      <SimpleField<SelectProps>
        name={name}
        label={label}
        helper={helper}
        height={height}
        {...rest}
        onChange={onChange}
        onBlur={onBlur}
      >
        <Box borderRadius={borderRadius} h={height}>
          <Select
            h={height}
            borderRadius={borderRadius}
            variant="filled"
            size={size}
            placeholder={placeholder}
            fontWeight="400"
            fontSize="sm"
            value={value ?? ''}
            isDisabled={rest.isDisabled}
            {...selectProps}
            {...formProps}
            isInvalid={isInvalid}
            _invalid={{
              borderColor: 'error',
            }}
            _focus={defaultStyles['field']._hover}
            _active={defaultStyles['field']._hover}
            ref={mergedRef}
          >
            <Show if={options?.length > 0}>
              {options.map((option) => (
                <option key={option.value} value={option.value} disabled={option?.disabled}>
                  {option.label}
                </option>
              ))}
            </Show>
            <Show if={optionGroupOptions?.length > 0}>
              {optionGroupOptions.map((optionGroup) => (
                <optgroup key={optionGroup?.groupLabel} label={optionGroup?.groupLabel}>
                  {optionGroup.options.map((option) => (
                    <option key={option.value} value={option.value} disabled={option?.disabled}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Show>
          </Select>
        </Box>
      </SimpleField>
    );
  },
);
